import React from 'react';
import type { User } from 'firebase/auth';
import type { SearchHistoryItem } from './RecentSearches';
import RecentSearches from './RecentSearches';
import SavedKeywords, { SavedKeywordItem } from './SavedKeywords';
import UsagePlanCard, { UsageSummary } from './UsagePlanCard';
import PlanUpgradeCard from './PlanUpgradeCard';

type Props = {
  user: User;
  historyItems: SearchHistoryItem[];
  historyLoading: boolean;
  savedKeywords: SavedKeywordItem[];
  savedKeywordsLoading: boolean;
  usage: UsageSummary | null;
  usageLoading: boolean;
  onRequestUpgrade: (plan: 'basic' | 'premium', message: string) => Promise<void>;
  onHistorySelect: (item: SearchHistoryItem) => void;
  onHistoryDelete: (item: SearchHistoryItem) => void;
  onSavedKeywordSelect: (item: SavedKeywordItem) => void;
  onSavedKeywordDelete: (item: SavedKeywordItem) => void;
  onBackToAnalysis: () => void;
  onLogout: () => void;
};

const MyPage: React.FC<Props> = ({
  user,
  historyItems,
  historyLoading,
  savedKeywords,
  savedKeywordsLoading,
  usage,
  usageLoading,
  onRequestUpgrade,
  onHistorySelect,
  onHistoryDelete,
  onSavedKeywordSelect,
  onSavedKeywordDelete,
  onBackToAnalysis,
  onLogout,
}) => {
  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-6">
      <div className="mb-6 flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-semibold text-blue-600">My Page</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">マイページ</h1>
          <p className="mt-2 text-sm text-gray-500">
            保存キーワードと検索履歴から、気になるテーマへすぐ戻れます。
          </p>
        </div>
        <button
          type="button"
          onClick={onBackToAnalysis}
          className="w-fit rounded bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600"
        >
          分析に戻る
        </button>
      </div>

      <section className="mb-6 rounded border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                className="h-14 w-14 rounded-full border border-gray-200"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-lg font-bold text-blue-600">
                {(user.displayName || user.email || 'U').slice(0, 1)}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-gray-900">
                {user.displayName || 'ログイン中'}
              </h2>
              {user.email && (
                <p className="truncate text-sm text-gray-500">{user.email}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="w-fit rounded border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            ログアウト
          </button>
        </div>
      </section>

      <div className="space-y-6">
        <UsagePlanCard usage={usage} loading={usageLoading} />

        <PlanUpgradeCard currentPlan={usage?.plan} onRequestUpgrade={onRequestUpgrade} />

        <SavedKeywords
          items={savedKeywords}
          loading={savedKeywordsLoading}
          onSelect={onSavedKeywordSelect}
          onDelete={onSavedKeywordDelete}
        />

        <RecentSearches
          items={historyItems}
          loading={historyLoading}
          onSelect={onHistorySelect}
          onDelete={onHistoryDelete}
          variant="page"
        />
      </div>
    </main>
  );
};

export default MyPage;
